import { ValidationMessage, ValidationProps } from 'ui';
import { ScreenField, ScreenPicker } from 'app/area/codetable/item/screen-renderer/types';
import { SafetyLimit } from 'app/area/codetable/item/types-store';
import { createSafetyLimitId } from 'app/area/codetable/item/codetable-item-helpers';

const SAFETY_LIMIT_FIELD_NAMES: Record<string, string> = {
  [ScreenPicker.minValue]: 'MinValue',
  [ScreenPicker.maxValue]: 'MaxValue',
  [ScreenField.warningDeviationPercent]: 'WarningDeviationPercent'
};

const ALL_SAFETY_LIMIT_FIELD_NAMES = Object.values(SAFETY_LIMIT_FIELD_NAMES);

const matchesField = (message: ValidationMessage, fieldName: string): boolean =>
  !message.fields?.length || message.fields.includes(fieldName);

const getServerValidation = (
  safetyLimitValidation: ValidationProps | undefined,
  appId: number,
  fieldName: string
): ValidationProps | undefined => {
  if (!safetyLimitValidation) {
    return undefined;
  }
  const matchingMessages = safetyLimitValidation.messages?.filter(
    m => m.parameters?.application === appId && matchesField(m, fieldName)
  );
  return matchingMessages?.length ? { ...safetyLimitValidation, messages: matchingMessages } : undefined;
};

export const getSafetyLimitValidation = (
  fieldKey: ScreenPicker | ScreenField,
  safetyLimit: SafetyLimit,
  validation: { [key: string]: ValidationProps }
): { fieldValidation: ValidationProps | undefined; serverValidation: ValidationProps | undefined } => {
  const appId = safetyLimit?.application?.value;
  const fieldValidation = validation?.[createSafetyLimitId(fieldKey, appId)];
  const serverValidation = getServerValidation(validation?.safetyLimit, appId, SAFETY_LIMIT_FIELD_NAMES[fieldKey]);
  return { fieldValidation, serverValidation };
};

const clearFieldFromMessage = (
  message: ValidationMessage,
  appId: number,
  fieldName: string
): ValidationMessage | undefined => {
  if (message.parameters?.application !== appId) {
    return message;
  }
  const remainingFields = (message.fields?.length ? message.fields : ALL_SAFETY_LIMIT_FIELD_NAMES)
    .filter(f => f !== fieldName);
  return remainingFields.length ? { ...message, fields: remainingFields } : undefined;
};

export const clearSafetyLimitServerValidationField = (
  safetyLimitValidation: ValidationProps | undefined,
  appId: number,
  fieldKey: ScreenPicker | ScreenField
): ValidationProps | undefined => {
  if (!safetyLimitValidation) {
    return undefined;
  }
  const fieldName = SAFETY_LIMIT_FIELD_NAMES[fieldKey];
  const messages = safetyLimitValidation.messages
    ?.map(message => clearFieldFromMessage(message, appId, fieldName))
    .filter(Boolean);
  return messages?.length ? { ...safetyLimitValidation, messages } : undefined;
};
